import { useMemo, useState } from 'react';

import {
  ChakraBox,
  ChakraFlex,
  CircularProgress,
  FileIconsV2,
  Text,
} from '@equip.health/ui';
import {
  AttachmentType,
  NoteAttachmentStatus,
} from '~/lib/constants/AddPatientNoteModal.constants';
import { MESSAGE_INVALID_FILE } from '~/lib/constants/s3filestatus.contants';
import { useNoteAttachmentModalContext } from '~/lib/contexts/NoteAttachmentModalContext';
import fileUtil, { getFileExtension } from '~/lib/util/file.util';
import { getFileIconV2 } from '~/lib/util/chat/chat.utils';
import { useLazyGetPatientDocumentDownloadUrlQuery } from '~/store/services/documents';

type PatientNoteAttachmentProps = {
  attachment: AttachmentType;
  id?: string;
  patientId: string;
};

const PatientNoteAttachment = ({
  attachment,
  id,
  patientId,
}: PatientNoteAttachmentProps) => {
  const { fileName, statusCode } = attachment;
  const [downloadFailed, setDownloadFailed] = useState<boolean>(false);
  const { setShowNoteAttachmentModal } = useNoteAttachmentModalContext();

  const [
    getNoteAttachmentDownloadUrl,
    {
      isLoading: isGetNoteAttachmentDownloadUrlLoading,
      isFetching: isGetNoteAttachmentDownloadUrlFetching,
      isError: isGetNoteAttachmentDownloadUrlError,
    },
  ] = useLazyGetPatientDocumentDownloadUrlQuery();

  const isFileUploaded = useMemo(() => {
    // When attachments are uploaded via the file-service, statusCode is null.
    // When uploaded via the clinical-data service, statusCode is UPLOADED.
    // We handle both cases by including attachments with either null or UPLOADED status.
    return statusCode === NoteAttachmentStatus.UPLOADED || statusCode === null;
  }, [statusCode]);

  let fileStatusText =
    getFileExtension(attachment.fileName)?.toUpperCase() ?? '';

  if (downloadFailed || isGetNoteAttachmentDownloadUrlError) {
    fileStatusText = MESSAGE_INVALID_FILE;
  }
  const openAttachment = (fileName: string, fileUrl: string) => {
    if (fileUtil.canShowPreview(fileName)) {
      setShowNoteAttachmentModal({
        name: fileName,
        url: fileUrl,
      });
      return;
    }
    window.open(fileUrl, '_blank', 'noopener');
  };

  const handleAttachmentClick = async () => {
    const hasAllDocumentInfo =
      fileName && attachment?.attachmentExternalId && patientId;

    if (!hasAllDocumentInfo || !isFileUploaded) return;

    setDownloadFailed(false);

    try {
      const response = await getNoteAttachmentDownloadUrl({
        documentExternalId: attachment.attachmentExternalId,
        fileName,
        patientId,
      }).unwrap();

      if (!response?.isValidFile || !response?.attachmentDownloadUrl) {
        setDownloadFailed(true);
        return;
      }
      openAttachment(fileName, response.attachmentDownloadUrl);
    } catch {
      setDownloadFailed(true);
    }
  };

  return (
    <ChakraBox marginBottom="12px">
      <ChakraFlex
        alignItems="center"
        background="neutral.background.primary"
        border="1px solid"
        borderColor="neutral.line"
        borderRadius="12px"
        id={id}
        margin="0 8px 0 0"
        padding="12px"
        maxWidth="280px"
        height="62px"
      >
        <ChakraBox height="fit-content" marginRight="12px">
          <FileIconsV2
            height="24px"
            icon={getFileIconV2(attachment.fileName)}
            width="24px"
          />
        </ChakraBox>

        <ChakraFlex
          cursor="pointer"
          flex="1 1 0"
          minWidth={0}
          onClick={handleAttachmentClick}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleAttachmentClick();
            }
          }}
          role="button"
          tabIndex={0}
          whiteSpace="nowrap"
          gap="2px"
          flexDirection="column"
        >
          <Text
            color="neutral.primary"
            overflow="hidden"
            textOverflow="ellipsis"
            variant="caption"
          >
            {attachment.fileName}
          </Text>
          <ChakraFlex color="neutral.secondary" lineHeight="18px">
            {!isGetNoteAttachmentDownloadUrlLoading && (
              <>
                <Text
                  variant="caption"
                  color="neutral.secondary"
                  marginRight="4px"
                >
                  {fileStatusText}
                </Text>
                {!downloadFailed && (
                  <Text variant="caption" color="neutral.secondary">
                    {fileUtil.isImageFile(attachment.fileName)
                      ? ' Image'
                      : ' Document'}
                  </Text>
                )}
              </>
            )}
          </ChakraFlex>
        </ChakraFlex>
        {(isGetNoteAttachmentDownloadUrlLoading ||
          isGetNoteAttachmentDownloadUrlFetching) && (
          <ChakraBox marginLeft="12px">
            <CircularProgress
              data-testid="patient-note-attachment-loading"
              id="patient-note-attachment-loading"
              isIndeterminate
              size="30px"
            />
          </ChakraBox>
        )}
      </ChakraFlex>
      {(isGetNoteAttachmentDownloadUrlError || downloadFailed) && (
        <ChakraBox color="error" fontSize="12px" marginBottom="12px">
          Unable to download attachment.
        </ChakraBox>
      )}
    </ChakraBox>
  );
};

export default PatientNoteAttachment;
